import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";

export interface Payment {
  id: number;
  name: string;
  name_trans: {
    ar: string;
  };
  status: boolean;
  created_at: string;
  updated_at: string;
}

interface PaymentsResponse {
  data: Payment[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// For autocomplete usage
export const usePaymentsForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["payments", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<PaymentsResponse> => {
      const response = await USERAXIOS.get<PaymentsResponse>(`/payments`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          // Assuming backend supports search by "title" or similar:
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  const payments = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: payments,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
